import { useMemo, useState } from "react";
import type { ClassRow, MonthRow, OfficerRow } from "./types";

export const C = {
  primary: "#1a78c2",
  primarySoft: "#dbeafe",
  accent: "#f97316",
  good: "#10b981",
  goodSoft: "#d1fae5",
  amber: "#f59e0b",
  text: "#475569",
  textDim: "#94a3b8",
  gridline: "rgba(148, 163, 184, 0.25)",
  narrative: "#1a78c2",
  interaction: "#7c3aed",
  textOnly: "#94a3b8",
};

function formatMonth(ym: string): string {
  const [y, m] = ym.split("-");
  const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return d.toLocaleString("en-US", { month: "short", year: "2-digit" });
}

export function classColor(c: string): string {
  if (c === "narrative") return C.narrative;
  if (c === "interaction") return C.interaction;
  if (c === "mixed") return C.accent;
  if (c === "audio_ai") return C.primary;
  if (c === "enhanced_writing") return "#0891b2";
  if (c === "ai_text") return "#64748b";
  return C.textOnly;
}

export function classLabel(c: string): string {
  if (c === "narrative") return "Narrative dictation";
  if (c === "interaction") return "Interaction / interview";
  if (c === "mixed") return "Mixed audio";
  if (c === "audio_ai") return "AI-assisted (audio)";
  if (c === "enhanced_writing") return "Enhanced writing";
  if (c === "ai_text") return "AI-assisted text";
  return "Manually typed";
}

// =============== Monthly time-saved trend ===============================

export function MonthlySavedTrend({ data, snapshotDate }: { data: MonthRow[]; snapshotDate?: string }) {
  const sorted = useMemo(() => [...data].sort((a, b) => a.month.localeCompare(b.month)), [data]);
  const width = 880;
  const height = 320;
  const pad = { top: 30, right: 60, bottom: 60, left: 50 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const maxSaved = Math.max(...sorted.map((d) => d.saved_min / 60), 1);
  const yMax = Math.ceil(maxSaved / 2) * 2;

  const xFor = (i: number) => {
    if (sorted.length <= 1) return pad.left + plotW / 2;
    return pad.left + (plotW * i) / (sorted.length - 1);
  };
  const yFor = (v: number) => pad.top + plotH * (1 - v / yMax);

  // Solid line covers all months up to but not including the last; the final
  // segment is dashed because the last month reflects only the data captured
  // before the snapshot date (typically a partial month).
  const lastIdx = sorted.length - 1;
  const solidPath = sorted
    .slice(0, Math.max(1, lastIdx))
    .map((d, i) => `${i === 0 ? "M" : "L"} ${xFor(i).toFixed(1)} ${yFor(d.saved_min / 60).toFixed(1)}`)
    .join(" ");
  const dashedSegment =
    lastIdx > 0
      ? `M ${xFor(lastIdx - 1).toFixed(1)} ${yFor(sorted[lastIdx - 1].saved_min / 60).toFixed(1)} L ${xFor(lastIdx).toFixed(1)} ${yFor(sorted[lastIdx].saved_min / 60).toFixed(1)}`
      : "";

  const area = `${solidPath} L ${xFor(Math.max(0, lastIdx - 1)).toFixed(1)} ${yFor(0)} L ${xFor(0).toFixed(1)} ${yFor(0)} Z`;

  const yTicks: number[] = [];
  for (let v = 0; v <= yMax; v += yMax / 4) yTicks.push(Math.round(v * 10) / 10);

  const [hover, setHover] = useState<number | null>(null);

  const snapshotLabel = snapshotDate
    ? new Date(snapshotDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
        <defs>
          <linearGradient id="savedFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={C.primary} stopOpacity="0.35" />
            <stop offset="100%" stopColor={C.primary} stopOpacity="0" />
          </linearGradient>
          <filter id="tip-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12" />
          </filter>
        </defs>

        {yTicks.map((v) => (
          <g key={v}>
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={yFor(v)}
              y2={yFor(v)}
              stroke={C.gridline}
              strokeDasharray="3 3"
            />
            <text x={pad.left - 8} y={yFor(v) + 4} fontSize="11" fill={C.textDim} textAnchor="end">
              {v}h
            </text>
          </g>
        ))}

        <path d={area} fill="url(#savedFill)" />
        <path d={solidPath} stroke={C.primary} strokeWidth="3" fill="none" strokeLinecap="round" />
        {dashedSegment && (
          <path
            d={dashedSegment}
            stroke={C.primary}
            strokeWidth="3"
            strokeDasharray="6 5"
            fill="none"
            strokeLinecap="round"
            opacity="0.7"
          />
        )}

        {/* Snapshot-date vertical marker at the last data point.
            Label is anchored on whichever side has room — defaults to LEFT
            since the marker usually sits at or near the right edge. */}
        {lastIdx >= 0 && (() => {
          const x = xFor(lastIdx);
          // 80px is roughly the rendered width of the longest label string.
          const labelOnRight = x + 80 < width - pad.right;
          const labelX = labelOnRight ? x + 6 : x - 6;
          const anchor = labelOnRight ? "start" : "end";
          return (
            <g>
              <line
                x1={x}
                x2={x}
                y1={pad.top}
                y2={pad.top + plotH}
                stroke={C.textDim}
                strokeDasharray="3 3"
                opacity="0.4"
              />
              <text
                x={labelX}
                y={pad.top + 4}
                fontSize="9"
                fill={C.textDim}
                fontStyle="italic"
                fontWeight="600"
                textAnchor={anchor}
              >
                Snapshot date
              </text>
              {snapshotLabel && (
                <text
                  x={labelX}
                  y={pad.top + 16}
                  fontSize="9"
                  fill={C.textDim}
                  textAnchor={anchor}
                >
                  {snapshotLabel}
                </text>
              )}
            </g>
          );
        })()}

        {sorted.map((d, i) => {
          const x = xFor(i);
          const y = yFor(d.saved_min / 60);
          const isLast = i === lastIdx;
          return (
            <g
              key={d.month}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: "pointer" }}
            >
              {/* Last point is shown as a hollow ring with a dashed outer halo
                  to signal "in progress / partial month" */}
              {isLast && (
                <circle cx={x} cy={y} r={10} fill="none" stroke={C.primary} strokeWidth="1.5" strokeDasharray="2 2" opacity="0.5" />
              )}
              <circle
                cx={x}
                cy={y}
                r={6}
                fill={isLast ? "white" : "white"}
                stroke={C.primary}
                strokeWidth={isLast ? 2.5 : 3}
                opacity={isLast ? 0.85 : 1}
              />
              <text x={x} y={height - pad.bottom + 20} fontSize="11" fill={C.text} textAnchor="middle">
                {formatMonth(d.month)}
              </text>
              <text
                x={x}
                y={y - 12}
                fontSize="11"
                fontWeight="600"
                fill={isLast ? C.textDim : C.primary}
                textAnchor="middle"
              >
                {(d.saved_min / 60).toFixed(1)}h
                {isLast && <tspan fontSize="9" fontStyle="italic"> (partial)</tspan>}
              </text>
            </g>
          );
        })}

        {hover !== null && (() => {
          const d = sorted[hover];
          const x = xFor(hover);
          const y = yFor(d.saved_min / 60);
          const w = 180;
          const h = 64;
          let tx = x + 12;
          if (tx + w > width) tx = x - w - 12;
          const ty = Math.max(pad.top, y - h - 12);
          return (
            <g style={{ pointerEvents: "none" }}>
              <rect x={tx} y={ty} width={w} height={h} rx="6" fill="white" stroke="#e2e8f0" filter="url(#tip-shadow)" />
              <text x={tx + 12} y={ty + 18} fontSize="12" fontWeight="600" fill="#0f172a">
                {formatMonth(d.month)}
              </text>
              <text x={tx + 12} y={ty + 35} fontSize="11" fill={C.text}>
                {d.cases} cases
              </text>
              <text x={tx + 12} y={ty + 51} fontSize="11" fontWeight="600" fill={C.primary}>
                {(d.saved_min / 60).toFixed(1)} hr saved · {d.words.toLocaleString()} words
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// =============== Workflow comparison bars (manual vs ai) ===============

export function WorkflowComparison({
  rows,
  obscureAudioSplit = false,
}: {
  rows: ClassRow[];
  obscureAudioSplit?: boolean;
}) {
  const ordered = useMemo(() => {
    if (obscureAudioSplit) {
      // Merge narrative + interaction + mixed (all audio-driven AI workflows)
      // into a single "AI-assisted (audio)" row. Enhanced writing and
      // ai_text stay as their own rows since they aren't audio-based.
      const audioRows = rows.filter(
        (r) => r.class === "narrative" || r.class === "interaction" || r.class === "mixed"
      );
      const others = rows.filter(
        (r) => r.class !== "narrative" && r.class !== "interaction" && r.class !== "mixed"
      );
      const merged: ClassRow[] = [];
      if (audioRows.length > 0) {
        merged.push({
          class: "audio_ai" as ClassRow["class"],
          cases: audioRows.reduce((s, r) => s + r.cases, 0),
          total_words: audioRows.reduce((s, r) => s + r.total_words, 0),
          avg_words:
            audioRows.reduce((s, r) => s + r.avg_words * r.cases, 0) /
            Math.max(1, audioRows.reduce((s, r) => s + r.cases, 0)),
          narrative_audio_sec: audioRows.reduce((s, r) => s + r.narrative_audio_sec, 0),
          interaction_audio_sec: audioRows.reduce((s, r) => s + r.interaction_audio_sec, 0),
          manual_min: audioRows.reduce((s, r) => s + r.manual_min, 0),
          ai_min: audioRows.reduce((s, r) => s + r.ai_min, 0),
          saved_min: audioRows.reduce((s, r) => s + r.saved_min, 0),
        });
      }
      const order: Record<string, number> = {
        audio_ai: 0,
        enhanced_writing: 1,
        ai_text: 2,
      };
      return [...merged, ...others].sort(
        (a, b) => (order[a.class] ?? 9) - (order[b.class] ?? 9)
      );
    }
    const order: Record<string, number> = {
      interaction: 0,
      narrative: 1,
      mixed: 2,
      enhanced_writing: 3,
      ai_text: 4,
    };
    return [...rows].sort((a, b) => (order[a.class] ?? 9) - (order[b.class] ?? 9));
  }, [rows, obscureAudioSplit]);

  const maxMin = Math.max(...ordered.map((r) => r.manual_min), 1);

  return (
    <div className="space-y-5">
      {ordered.map((r) => {
        const manualPct = (r.manual_min / maxMin) * 100;
        const aiPct = (r.ai_min / maxMin) * 100;
        const savedPct = Math.max(0, ((r.manual_min - r.ai_min) / maxMin) * 100);
        return (
          <div key={r.class}>
            <div className="flex items-baseline justify-between mb-2">
              <div className="flex items-center gap-2">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: classColor(r.class) }}
                />
                <span className="text-sm font-semibold text-slate-800">{classLabel(r.class)}</span>
                <span className="text-xs text-slate-500">{r.cases} cases · {r.total_words.toLocaleString()} words</span>
              </div>
              <span className="text-sm font-semibold tabular-nums" style={{ color: classColor(r.class) }}>
                {(r.saved_min / 60).toFixed(1)} hr saved
              </span>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 w-16 shrink-0">Manual</span>
                <div className="flex-1 h-5 bg-slate-100 rounded-md overflow-hidden relative">
                  <div
                    className="h-full bg-slate-300 transition-all duration-1000 ease-out"
                    style={{ width: `${manualPct}%` }}
                  />
                  <div
                    className="absolute top-0 right-0 h-full px-2 flex items-center text-[11px] font-mono text-slate-700"
                    style={{ left: `${manualPct}%` }}
                  >
                    {(r.manual_min / 60).toFixed(1)}h
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 w-16 shrink-0">With AI</span>
                <div className="flex-1 h-5 bg-slate-100 rounded-md overflow-hidden relative">
                  <div
                    className="h-full transition-all duration-1000 ease-out"
                    style={{ width: `${aiPct}%`, backgroundColor: classColor(r.class) }}
                  />
                  <div
                    className="absolute top-0 right-0 h-full px-2 flex items-center text-[11px] font-mono"
                    style={{ left: `${aiPct}%`, color: classColor(r.class) }}
                  >
                    {(r.ai_min / 60).toFixed(1)}h
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider w-16 shrink-0 text-emerald-600 font-semibold">Saved</span>
                <div className="flex-1 h-2 bg-emerald-50 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${savedPct}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============== Officer leaderboard bars ===============================

export function OfficerLeaderboard({ data, limit = 15 }: { data: OfficerRow[]; limit?: number }) {
  const top = useMemo(
    () =>
      [...data]
        .filter((o) => o.total_cases > 0)
        .sort((a, b) => b.saved_min - a.saved_min || b.total_cases - a.total_cases)
        .slice(0, limit),
    [data, limit]
  );

  if (top.length === 0) return <div className="text-sm text-slate-500">No officers found.</div>;

  const maxSaved = Math.max(...top.map((o) => o.saved_min), 1);

  return (
    <div className="divide-y divide-slate-100">
      {top.map((o, i) => {
        const widthPct = (o.saved_min / maxSaved) * 100;
        return (
          <div key={o.user_id} className="py-3 flex items-center gap-4">
            <div className="w-7 text-center text-sm font-mono tabular-nums text-slate-400 shrink-0">
              {i + 1}
            </div>
            <div className="w-44 shrink-0 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{o.officer_name}</div>
              <div className="text-[11px] text-slate-500">
                {o.badge_id ? `#${o.badge_id} · ` : ""}
                <span className="font-semibold text-slate-700">{o.total_cases}</span> reports
                {o.typed_cases > 0 && (
                  <span className="text-slate-400"> ({o.cases} AI · {o.typed_cases} typed)</span>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="h-6 bg-slate-100 rounded-md overflow-hidden relative">
                <div
                  className="h-full transition-all duration-[1400ms] ease-out"
                  style={{
                    width: `${widthPct}%`,
                    background:
                      i === 0
                        ? `linear-gradient(90deg, ${C.primary}, ${C.accent})`
                        : i < 3
                          ? C.primary
                          : "#cbd5e1",
                  }}
                />
                <div className="absolute inset-y-0 left-3 right-3 flex items-center justify-between text-[11px] font-mono text-slate-600 pointer-events-none">
                  <span className="text-white drop-shadow-sm font-semibold mix-blend-difference">
                    {o.words.toLocaleString()}w
                  </span>
                </div>
              </div>
            </div>
            <div className="w-24 text-right shrink-0">
              <div className="text-sm font-bold tabular-nums text-emerald-600">
                {(o.saved_min / 60).toFixed(1)}h
              </div>
              <div className="text-[10px] text-slate-400">saved</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============== Audio breakdown =======================================

export function AudioBreakdown({
  narrativeSec,
  interactionSec,
  obscureAudioSplit = false,
}: {
  narrativeSec: number;
  interactionSec: number;
  obscureAudioSplit?: boolean;
}) {
  const total = narrativeSec + interactionSec;
  if (total === 0) return null;

  if (obscureAudioSplit) {
    // Single combined "audio captured" card — no narrative/interaction split.
    return (
      <div>
        <div className="h-9 rounded-md overflow-hidden flex shadow-sm">
          <div
            className="flex items-center justify-center text-xs font-semibold text-white transition-all duration-1000 w-full"
            style={{ backgroundColor: C.primary }}
          >
            {`${(total / 3600).toFixed(1)} hr audio captured`}
          </div>
        </div>
        <div className="mt-4 border border-slate-200 rounded-md p-3">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: C.primary }}
            />
            <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
              Audio captured
            </span>
          </div>
          <div className="text-xl font-semibold tabular-nums text-slate-900">
            {(total / 3600).toFixed(1)}{" "}
            <span className="text-sm text-slate-500 font-normal">hours captured</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Audio that officers captured during the trial — transcribed and routed
            into the AI report-drafting workflow.
          </div>
        </div>
      </div>
    );
  }

  const narrPct = (narrativeSec / total) * 100;
  const interPct = (interactionSec / total) * 100;

  return (
    <div>
      <div className="h-9 rounded-md overflow-hidden flex shadow-sm">
        <div
          className="flex items-center justify-center text-xs font-semibold text-white transition-all duration-1000"
          style={{ width: `${narrPct}%`, backgroundColor: C.narrative }}
        >
          {narrPct >= 12 && `${(narrativeSec / 3600).toFixed(1)} hr narrative`}
        </div>
        <div
          className="flex items-center justify-center text-xs font-semibold text-white transition-all duration-1000"
          style={{ width: `${interPct}%`, backgroundColor: C.interaction }}
        >
          {interPct >= 12 && `${(interactionSec / 3600).toFixed(1)} hr interaction`}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="border border-slate-200 rounded-md p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: C.narrative }} />
            <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Narrative dictation</span>
          </div>
          <div className="text-xl font-semibold tabular-nums text-slate-900">
            {(narrativeSec / 3600).toFixed(1)} <span className="text-sm text-slate-500 font-normal">hours captured</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Officers spoke their reports instead of typing them. AI converted speech into a narrative.
          </div>
        </div>
        <div className="border border-slate-200 rounded-md p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: C.interaction }} />
            <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Interaction / body cam</span>
          </div>
          <div className="text-xl font-semibold tabular-nums text-slate-900">
            {(interactionSec / 3600).toFixed(1)} <span className="text-sm text-slate-500 font-normal">hours processed</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Interview and body cam audio that AI transcribed and summarized — no re-listening required.
          </div>
        </div>
      </div>
    </div>
  );
}

// =============== Throughput by month bars ===============================

export function ThroughputBars({ data }: { data: MonthRow[] }) {
  const sorted = useMemo(() => [...data].sort((a, b) => a.month.localeCompare(b.month)), [data]);
  const width = 880;
  // Extra height + bottom padding so the legend doesn't crowd the bar labels.
  const height = 290;
  const pad = { top: 20, right: 20, bottom: 80, left: 40 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  // Use total_cases (AI + typed) for the y-axis scale so stacked bars fit.
  const max = Math.max(...sorted.map((d) => d.total_cases ?? d.cases), 1);
  const barW = plotW / sorted.length - 14;
  const colW = plotW / sorted.length;

  const [hover, setHover] = useState<number | null>(null);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
      <defs>
        <filter id="throughput-tip-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12" />
        </filter>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
        const v = Math.round(max * frac);
        const y = pad.top + plotH * (1 - frac);
        return (
          <g key={frac}>
            <line x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke={C.gridline} strokeDasharray="3 3" />
            <text x={pad.left - 8} y={y + 4} fontSize="11" fill={C.textDim} textAnchor="end">
              {v}
            </text>
          </g>
        );
      })}
      {sorted.map((row, i) => {
        const x = pad.left + (plotW * i) / sorted.length + 7;
        const totalCases = row.total_cases ?? row.cases;
        const typedCases = row.typed_cases ?? 0;
        const aiH = (row.cases / max) * plotH;
        const typedH = (typedCases / max) * plotH;
        const totalH = aiH + typedH;
        const aiY = pad.top + plotH - aiH;
        const typedY = pad.top + plotH - totalH;
        const isLast = i === sorted.length - 1;
        return (
          <g
            key={row.month}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            style={{ cursor: "pointer" }}
          >
            {/* Typed (stacked on top, lighter) */}
            {typedH > 0 && (
              <rect
                x={x}
                y={typedY}
                width={barW}
                height={typedH}
                fill={isLast ? "white" : "#94a3b8"}
                stroke={isLast ? "#94a3b8" : "none"}
                strokeWidth={isLast ? 1.5 : 0}
                strokeDasharray={isLast ? "4 3" : undefined}
                rx="3"
                opacity={isLast ? 0.6 : 0.85}
              />
            )}
            {/* AI-assisted (bottom, primary) */}
            <rect
              x={x}
              y={aiY}
              width={barW}
              height={aiH}
              fill={isLast ? "white" : C.primary}
              stroke={isLast ? C.primary : "none"}
              strokeWidth={isLast ? 1.5 : 0}
              strokeDasharray={isLast ? "4 3" : undefined}
              rx="3"
              opacity={isLast ? 0.7 : 0.85}
            />
            <text
              x={x + barW / 2}
              y={typedY - 5}
              fontSize="11"
              fontWeight="600"
              fill={isLast ? C.textDim : C.primary}
              textAnchor="middle"
            >
              {totalCases}
              {isLast && <tspan fontSize="9" fontStyle="italic"> (partial)</tspan>}
            </text>
            <text x={x + barW / 2} y={height - pad.bottom + 18} fontSize="11" fill={C.text} textAnchor="middle">
              {formatMonth(row.month)}
            </text>
            {/* Invisible column overlay — makes the full column hover-able,
                not just the rendered bar segments. */}
            <rect
              x={x - 7}
              y={pad.top}
              width={colW}
              height={plotH}
              fill="transparent"
            />
          </g>
        );
      })}
      {hover !== null && (() => {
        const row = sorted[hover];
        const totalCases = row.total_cases ?? row.cases;
        const typedCases = row.typed_cases ?? 0;
        const aiCases = row.cases;
        const isLast = hover === sorted.length - 1;
        const x = pad.left + (plotW * hover) / sorted.length + 7;
        const w = 200;
        const h = 88;
        let tx = x + barW + 8;
        if (tx + w > width - pad.right) tx = x - w - 8;
        const ty = pad.top + 8;
        return (
          <g style={{ pointerEvents: "none" }}>
            <rect
              x={tx}
              y={ty}
              width={w}
              height={h}
              rx="6"
              fill="white"
              stroke="#e2e8f0"
              filter="url(#throughput-tip-shadow)"
            />
            <text x={tx + 12} y={ty + 18} fontSize="12" fontWeight="600" fill="#0f172a">
              {formatMonth(row.month)}
              {isLast && <tspan fontSize="9" fontStyle="italic" fill={C.textDim}> (partial)</tspan>}
            </text>
            <g transform={`translate(${tx + 12}, ${ty + 34})`}>
              <rect x="0" y="-8" width="9" height="9" fill={C.primary} rx="2" opacity="0.85" />
              <text x="14" y="0" fontSize="11" fill={C.text}>
                AI-assisted: <tspan fontWeight="600" fill="#0f172a">{aiCases}</tspan>
              </text>
            </g>
            <g transform={`translate(${tx + 12}, ${ty + 51})`}>
              <rect x="0" y="-8" width="9" height="9" fill="#94a3b8" rx="2" opacity="0.85" />
              <text x="14" y="0" fontSize="11" fill={C.text}>
                Typed: <tspan fontWeight="600" fill="#0f172a">{typedCases}</tspan>
              </text>
            </g>
            <text x={tx + 12} y={ty + 73} fontSize="11" fontWeight="600" fill={C.primary}>
              Total: {totalCases} cases
            </text>
          </g>
        );
      })()}
      {/* Legend — positioned in the extra bottom pad area, well clear of bar labels */}
      <g transform={`translate(${pad.left},${height - 12})`}>
        <rect x="0" y="-9" width="10" height="10" fill={C.primary} rx="2" opacity="0.85" />
        <text x="14" y="0" fontSize="10" fill={C.text}>AI-assisted</text>
        <rect x="90" y="-9" width="10" height="10" fill="#94a3b8" rx="2" opacity="0.85" />
        <text x="104" y="0" fontSize="10" fill={C.text}>Typed in platform</text>
      </g>
    </svg>
  );
}

// =============== Score ring (used in case viewer) =====================

export function ScoreRing({ score, size = 80, label }: { score: number | null; size?: number; label?: string }) {
  const pct = score == null ? 0 : Math.max(0, Math.min(100, score));
  const stroke = 8;
  const r = size / 2 - stroke;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const color =
    pct >= 85 ? "#10b981" : pct >= 75 ? C.primary : pct >= 65 ? "#f59e0b" : "#ef4444";
  return (
    <div className="inline-flex flex-col items-center" style={{ width: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text
          x={size / 2}
          y={size / 2 + 5}
          textAnchor="middle"
          fontSize={size / 3.2}
          fontWeight="700"
          fill="#0f172a"
        >
          {score == null ? "n/a" : Math.round(score)}
        </text>
      </svg>
      {label && <div className="text-xs text-slate-500 mt-1">{label}</div>}
    </div>
  );
}
